import { Component, OnDestroy, OnInit } from '@angular/core';
import { User } from '../models/index';
import { Router } from '@angular/router';
import { UserService } from '../services/index';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as bootstrap from 'bootstrap';

interface AccountActivityItem {
  id: string;
  icon: string;
  color: string;
  title: string;
  time: string;
  body: string;
  timestamp: number;
}

interface NotificationPreferenceState {
  tripUpdates: { email: boolean; sms: boolean; push: boolean };
  priceDrops: { email: boolean; sms: boolean; push: boolean };
  promotions: { email: boolean; sms: boolean; push: boolean };
  accountSecurity: { email: boolean; sms: boolean; push: boolean };
}

interface ExportPreviewRow {
  label: string;
  value: string;
}

@Component({
    selector: 'app-myaccount',
    templateUrl: './myaccount.component.html',
    styleUrls: ['./myaccount.component.css'],
    standalone: false
})
export class MyaccountComponent implements OnInit, OnDestroy {
  isLoggedIn: any;
  user: User;
  readonly flightTripsAvailable = false;
  modalTitle: string = '';
  modalValue: string = '';
  activeTab: string = 'profile'; // profile | trips | login | devices
  emailInput: string = '';
  profileCompletion: number = 0;

  // ── Trips state ──────────────────────────────────────
  trips: any[] = [];
  tripsLoading = false;
  tripsFetched = false;
  filterTab: 'upcoming' | 'completed' | 'cancelled' = 'upcoming';
  expandedIndex: number | null = null;

  // ── Notifications state ───────────────────────────────
  notifPrefs = {
    tripUpdates:    { email: true,  sms: false, push: false },
    priceDrops:     { email: false, sms: false, push: true  },
    promotions:     { email: true,  sms: false, push: true  },
    accountSecurity:{ email: true,  sms: false, push: false },
  };
  quietHours = true;
  lastActivityReadAt = 0;

  recentActivity: AccountActivityItem[] = [];
  loggedInDevices: any[] = [];
  isDataExportModalOpen = false;
  private lastFocusedElement: HTMLElement | null = null;

  form = {
    first_name: '',
    last_name: '',
    email: '',
    id_number: ''
  };

  passwordForm = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  };

  showOldPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  phoneNumber: string = '';
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private userService: UserService,
  ) {}

  fetchLoginHistory() {
    // Assuming you're storing user login history in `user.login_history`
    const loginHistory = this.user?.login_history || [];

    this.loggedInDevices = loginHistory.map((entry: any) => {
      return {
        deviceType: entry.device_info || 'Unknown Device',
        location: 'Nairobi', // Or parse from IP using a location service
        ipAddress: entry.ip_address,
        lastActive: this.formatDate(entry.login_time),
        isCurrent: this.isCurrentDevice(entry)
      };
    });

    this.buildRecentActivity();
  }
  validateEmail(email: string): boolean {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString(); // You can customize this format
  }

  isCurrentDevice(entry: any): boolean {
    // Simple logic: match IP address and user agent
    const currentIp = sessionStorage.getItem('current_ip'); // You might fetch this on login
    const currentAgent = navigator.userAgent;

    return (
      entry.ip_address === currentIp &&
      (entry.device_info || '').includes(this.getBrowserName())
    );
  }

  getBrowserName(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown';
  }
  ngOnInit(): void {
    this.userService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((userData) => {
        this.isLoggedIn = this.userService.isAuthenticated();

        if (!this.isLoggedIn || !userData) {
          this.router.navigateByUrl('/');
          return;
        }

        this.user = new User().deserialize(userData);
        this.populateFormFromUser();
        this.calculateProfileCompletion();
        this.loadNotificationSettings();
        this.fetchLoginHistory();
      });

    this.userService.refreshAuthState();
  }

  ngOnDestroy(): void {
    this.closeDataExportModal();
    this.destroy$.next();
    this.destroy$.complete();
  }
  

  populateFormFromUser(): void {
    if (this.user) {
      const nameParts = this.user.fullName?.split(' ') || [];
      this.form.first_name = nameParts[0] || '';
      this.form.last_name = nameParts[1] || '';
      this.form.email = this.user.email || '';
      this.form.id_number = this.user.id_number || '';
    }
  }

  calculateProfileCompletion(): void {
    const fields = [
      this.form.first_name,
      this.form.last_name,
      this.form.email,
      this.form.id_number,
      this.user?.phone_number
    ];
    const filled = fields.filter(f => f && f.toString().trim() !== '').length;
    const total = fields.length;
    this.profileCompletion = Math.round((filled / total) * 100);
  }

  scrollTo(section: string) {
    this.activeTab = section;
    if (section === 'trips' && this.flightTripsAvailable && !this.tripsFetched) {
      const phone = '0' + this.user.phone_number.substring(this.user.phone_number.length - 9);
      this.loadTrips(phone);
    }
  }

  loadTrips(phone: string) {
    this.tripsLoading = true;
    this.userService.getTrips(phone).subscribe(
      (res: any) => {
        this.tripsLoading = false;
        this.tripsFetched = true;
        this.trips = res?.rows || [];
        this.buildRecentActivity();
      },
      () => {
        this.tripsLoading = false;
        this.tripsFetched = true;
        this.buildRecentActivity();
      }
    );
  }

  setFilter(tab: 'upcoming' | 'completed' | 'cancelled') {
    this.filterTab = tab;
    this.expandedIndex = null;
  }

  toggleExpand(index: number) {
    this.expandedIndex = this.expandedIndex === index ? null : index;
  }

  tripStatus(trip: any): string {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const depDate = trip.departure_date ? new Date(trip.departure_date) : null;
    if (depDate && depDate < today) return 'Completed';
    if (trip.confirmed === false) return 'Cancelled';
    return 'Upcoming';
  }

  get filteredTrips(): any[] {
    return this.trips.filter(t => this.tripStatus(t).toLowerCase() === this.filterTab);
  }
  get upcomingCount()  { return this.trips.filter(t => this.tripStatus(t) === 'Upcoming').length; }
  get completedCount() { return this.trips.filter(t => this.tripStatus(t) === 'Completed').length; }
  get cancelledCount() { return this.trips.filter(t => this.tripStatus(t) === 'Cancelled').length; }
  get totalSpent(): number { return this.trips.reduce((s, t) => s + (parseFloat(t.total_amount) || 0), 0); }
  get uniqueOperators(): number { return new Set(this.trips.map(t => t.operator).filter(Boolean)).size; }
  get unreadActivityCount(): number {
    return this.recentActivity.filter(item => item.timestamp > this.lastActivityReadAt).length;
  }

  routeFrom(route: string): string {
    if (!route) return '—';
    return route.split(/\s*[-–]\s*/)[0]?.trim() || route;
  }
  routeTo(route: string): string {
    if (!route) return '—';
    return route.split(/\s*[-–]\s*/)[1]?.trim() || route;
  }

  openModal(field: string) {
    this.modalTitle = field;
    this.modalValue = '';
    const modalElement = document.getElementById('buupassProfileModal');
    if (modalElement) {
      const bsModal = new bootstrap.Modal(modalElement);
      bsModal.show();
    }
  }

  submitProfile() {
    if (!this.form.first_name || !this.form.last_name ) {
      alert('First name and  Last name are required!');
      return;
    }

    this.userService.updateProfile(this.form).subscribe({
      next: (response: any) => {
        alert('Profile updated successfully!');
        const updatedUser = { ...this.user, ...this.form };
        this.userService.setAuthenticatedUser(updatedUser);
        const modalElement = document.getElementById('buupassProfileModal');
        if (modalElement) {
          const modalInstance = bootstrap.Modal.getInstance(modalElement);
          modalInstance.hide();
        }
        this.user = new User().deserialize(updatedUser);
        this.calculateProfileCompletion();
      },
      error: (error) => {
        console.error('Update failed:', error);
        alert('Failed to update profile. Please try again.');
      }
    });
  }

  submitEmail() {
    if (!this.emailInput || !this.validateEmail(this.emailInput)) {
      alert('Please enter a valid email.');
      return;
    }
  
    this.userService.updateProfile({ email: this.emailInput }).subscribe({
      next: (response: any) => {
        alert('Email updated successfully!');
        
        const updatedUser = { ...this.user, email: this.emailInput };
        this.userService.setAuthenticatedUser(updatedUser);
        this.user = new User().deserialize(updatedUser);
  
        const modalElement = document.getElementById('buupassEmailModal');
        if (modalElement) {
          const modalInstance = bootstrap.Modal.getInstance(modalElement);
          modalInstance?.hide();
        }
  
        this.calculateProfileCompletion();
      },
      error: (error) => {
        console.error('Email update failed:', error);
        alert('Failed to update email. Please try again.');
      }
    });
  }
  
  submitPassword() {
    const { oldPassword, newPassword, confirmPassword } = this.passwordForm;

    if (!oldPassword || !newPassword || !confirmPassword) {
      alert('Please fill all fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      alert('New passwords do not match.');
      return;
    }

    if (newPassword.length < 6) {
      alert('Password should be at least 6 characters.');
      return;
    }

    alert('Password changed successfully!');
    const modal = document.getElementById('buupassPasswordModal');
    if (modal) {
      const instance = bootstrap.Modal.getInstance(modal);
      instance.hide();
    }
    this.calculateProfileCompletion();
  }

  submitPhoneNumber() {
    const phoneRegex = /^[0-9]{9}$/;
    if (!phoneRegex.test(this.phoneNumber)) {
      alert('Please enter a valid 9-digit mobile number.');
      return;
    }

    const fullNumber = '+254' + this.phoneNumber;
    console.log('Saved phone number:', fullNumber);
    alert(`Mobile number ${fullNumber} saved successfully!`);
    const modalElement = document.getElementById('buupassPhoneModal');
    if (modalElement) {
      const modalInstance = bootstrap.Modal.getInstance(modalElement);
      modalInstance.hide();
    }
    this.calculateProfileCompletion();
  }

  logoutUser() {
    this.userService.logout();
    this.isLoggedIn = false;
    this.recentActivity = [];
    this.router.navigate(['/']);
  }

  openDataExportModal(): void {
    this.lastFocusedElement = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    this.isDataExportModalOpen = true;
    document.body.classList.add('modal-open');
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      const closeButton = document.querySelector('#buupassDataExportModal .close') as HTMLElement | null;
      const modalElement = document.getElementById('buupassDataExportModal');
      (closeButton || modalElement)?.focus();
    });
  }

  closeDataExportModal(): void {
    const modalElement = document.getElementById('buupassDataExportModal');
    const activeElement = document.activeElement as HTMLElement | null;

    if (modalElement && activeElement && modalElement.contains(activeElement)) {
      activeElement.blur();
    }

    this.isDataExportModalOpen = false;
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';

    setTimeout(() => {
      this.lastFocusedElement?.focus();
      this.lastFocusedElement = null;
    });
  }

  downloadAccountData(): void {
    if (!this.user) {
      return;
    }

    const exportPayload = this.buildAccountExportPayload();

    const safeName = (this.user.fullName || 'account')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'account';

    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `buupass-${safeName}-data.json`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    this.closeDataExportModal();
  }

  get exportProfileRows(): ExportPreviewRow[] {
    return [
      { label: 'Name', value: this.user?.fullName || '—' },
      { label: 'Phone', value: this.user?.phone_number || '—' },
      { label: 'Email', value: this.user?.email || '—' },
      { label: 'ID number', value: this.maskValue(this.user?.id_number) },
      { label: 'Profile completion', value: `${this.profileCompletion}%` },
    ];
  }

  get exportNotificationRows(): ExportPreviewRow[] {
    return [
      { label: 'Trip updates email', value: this.notifPrefs.tripUpdates.email ? 'Enabled' : 'Disabled' },
      { label: 'Account security email', value: this.notifPrefs.accountSecurity.email ? 'Enabled' : 'Disabled' },
      { label: 'Quiet hours', value: this.quietHours ? 'Enabled' : 'Disabled' },
      { label: 'Unread activity', value: `${this.unreadActivityCount}` },
    ];
  }

  get exportTripPreview(): Array<{ key: string; title: string; meta: string }> {
    return this.trips.slice(0, 3).map((trip, index) => ({
      key: trip?.trade_id || trip?.booking_reference || `${trip?.source || 'trip'}-${trip?.departure_date || 'date'}-${index}`,
      title: `${trip?.source || 'Origin'} → ${trip?.destination || 'Destination'}`,
      meta: `${trip?.departure_date || '—'} · ${trip?.operator || 'Operator'} · Ksh ${Number(trip?.total_amount || 0).toLocaleString()}`,
    }));
  }

  get exportDevicePreview(): Array<{ key: string; title: string; meta: string }> {
    return this.loggedInDevices.slice(0, 3).map((device, index) => ({
      key: `${device?.deviceType || 'device'}-${device?.ipAddress || 'ip'}-${device?.lastActive || 'time'}-${index}`,
      title: device?.deviceType || 'Unknown device',
      meta: `${device?.lastActive || '—'} · ${device?.ipAddress || 'No IP recorded'}`,
    }));
  }

  get exportDataSectionsCount(): number {
    return 5;
  }

  onNotificationPreferencesChange(): void {
    this.persistNotificationSettings();
  }

  markAllActivityRead(): void {
    const latestTimestamp = this.recentActivity.reduce((latest, item) => Math.max(latest, item.timestamp), 0);
    this.lastActivityReadAt = latestTimestamp || Date.now();
    this.persistNotificationSettings();
  }

  private buildRecentActivity(): void {
    const activities: AccountActivityItem[] = [];
    const now = Date.now();

    (this.user?.login_history || []).forEach((entry: any, index: number) => {
      const timestamp = this.parseTimestamp(entry?.login_time);
      if (!timestamp) {
        return;
      }

      const deviceName = entry?.device_info || 'Unknown device';
      const ipLabel = entry?.ip_address ? `IP ${entry.ip_address}` : 'Account access recorded';

      activities.push({
        id: `login-${timestamp}-${index}`,
        icon: '🖥️',
        color: '#F3F4F6',
        title: this.isCurrentDevice(entry) ? 'Current device active' : 'New device sign-in',
        time: this.toRelativeTime(timestamp),
        body: `${deviceName} · ${ipLabel}`,
        timestamp,
      });
    });

    this.trips.forEach((trip: any, index: number) => {
      const tripRef = trip?.trade_id || trip?.booking_reference || `trip-${index}`;
      const routeLabel = `${trip?.source || 'Origin'} to ${trip?.destination || 'Destination'}`;
      const operatorLabel = trip?.operator ? ` with ${trip.operator}` : '';
      const seatLabel = trip?.seats?.[0] ? ` · Seat ${trip.seats[0]}` : '';

      const createdAt = this.parseTimestamp(trip?.created_at);
      if (createdAt) {
        activities.push({
          id: `booking-${tripRef}`,
          icon: '🎫',
          color: '#FEF3ED',
          title: 'Trip booked',
          time: this.toRelativeTime(createdAt),
          body: `${routeLabel}${operatorLabel}`,
          timestamp: createdAt,
        });
      }

      const departureTs = this.parseTripDateTime(trip?.departure_date, trip?.departure_time);
      if (departureTs && departureTs > now) {
        const hoursUntilDeparture = (departureTs - now) / (1000 * 60 * 60);
        if (hoursUntilDeparture <= 24) {
          activities.push({
            id: `departure-${tripRef}`,
            icon: '🚌',
            color: '#FEF3ED',
            title: 'Trip departs soon',
            time: this.toRelativeTime(departureTs),
            body: `${routeLabel}${seatLabel}${operatorLabel}`,
            timestamp: departureTs,
          });
        }
      }
    });

    this.recentActivity = activities
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 5);
  }

  private loadNotificationSettings(): void {
    const storageKey = this.getNotificationSettingsStorageKey();
    if (!storageKey) {
      return;
    }

    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw);
      if (parsed?.notifPrefs) {
        this.notifPrefs = {
          ...this.notifPrefs,
          ...parsed.notifPrefs,
        };
      }
      if (typeof parsed?.quietHours === 'boolean') {
        this.quietHours = parsed.quietHours;
      }
      if (typeof parsed?.lastActivityReadAt === 'number') {
        this.lastActivityReadAt = parsed.lastActivityReadAt;
      }
    } catch (error) {
      console.warn('Unable to read notification settings', error);
    }
  }

  private persistNotificationSettings(): void {
    const storageKey = this.getNotificationSettingsStorageKey();
    if (!storageKey) {
      return;
    }

    const payload = {
      notifPrefs: this.notifPrefs,
      quietHours: this.quietHours,
      lastActivityReadAt: this.lastActivityReadAt,
    };

    localStorage.setItem(storageKey, JSON.stringify(payload));
  }

  private getNotificationSettingsStorageKey(): string | null {
    const userKey = this.user?.phone_number || this.user?.email;
    return userKey ? `bp-account-notifications:${userKey}` : null;
  }

  private parseTimestamp(value: string | null | undefined): number | null {
    if (!value) {
      return null;
    }

    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  private parseTripDateTime(dateValue: string | null | undefined, timeValue: string | null | undefined): number | null {
    if (!dateValue) {
      return null;
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    const dateKey = typeof dateValue === 'string' && dateValue.length >= 10
      ? dateValue.slice(0, 10)
      : parsedDate.toISOString().slice(0, 10);

    const rawTime = (timeValue || '00:00:00').trim();
    const normalizedTime = /^\d{2}:\d{2}$/.test(rawTime)
      ? `${rawTime}:00`
      : (/^\d{2}:\d{2}:\d{2}$/.test(rawTime) ? rawTime : '00:00:00');

    const timestamp = new Date(`${dateKey}T${normalizedTime}`).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  private toRelativeTime(timestamp: number): string {
    const diffMs = timestamp - Date.now();
    const diffMinutes = Math.round(diffMs / (1000 * 60));
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

    if (Math.abs(diffMinutes) < 1) {
      return 'just now';
    }
    if (Math.abs(diffMinutes) < 60) {
      return rtf.format(diffMinutes, 'minute');
    }
    if (Math.abs(diffHours) < 24) {
      return rtf.format(diffHours, 'hour');
    }
    return rtf.format(diffDays, 'day');
  }

  private buildAccountExportPayload() {
    return {
      exported_at: new Date().toISOString(),
      profile: {
        full_name: this.user?.fullName || '',
        first_name: this.user?.first_name || '',
        last_name: this.user?.last_name || '',
        email: this.user?.email || '',
        phone_number: this.user?.phone_number || '',
        id_number: this.user?.id_number || '',
        profile_completion: this.profileCompletion,
      },
      notification_preferences: {
        trip_updates_email: this.notifPrefs.tripUpdates.email,
        account_security_email: this.notifPrefs.accountSecurity.email,
        quiet_hours_enabled: this.quietHours,
      },
      devices: this.loggedInDevices.map((device) => ({
        device_type: device?.deviceType || '',
        ip_address: device?.ipAddress || '',
        last_active: device?.lastActive || '',
        is_current: Boolean(device?.isCurrent),
      })),
      recent_activity: this.recentActivity.map((item) => ({
        title: item.title,
        time: item.time,
        detail: item.body,
      })),
      trips: this.trips.map((trip) => ({
        booking_reference: trip?.trade_id || trip?.booking_reference || '',
        departure_date: trip?.departure_date || '',
        departure_time: trip?.departure_time || '',
        arrival_time: trip?.arrival_time || '',
        source: trip?.source || '',
        destination: trip?.destination || '',
        operator: trip?.operator || '',
        seat: trip?.seats?.[0] || '',
        amount: trip?.total_amount || 0,
        payment_channel: trip?.payment_channel || '',
        status: this.tripStatus(trip),
      })),
    };
  }

  private maskValue(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }
    if (value.length <= 4) {
      return value;
    }
    return `${value.slice(0, 3)}***${value.slice(-1)}`;
  }
}
